import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api } from '@/api/http';
import { AdminPageHeader } from '@/components/admin/admin-shell';
import { tokenStore } from '@/lib/auth';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/admin/leads')({ component: AdminLeads });

type Lead = {
  id: string;
  name: string;
  email: string;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  referrer: string | null;
  createdAt: string;
};

function AdminLeads() {
  const { data: leads = [], isLoading } = useQuery({
    queryKey: ['admin-leads'],
    queryFn: () => api.get('admin/leads').json<Lead[]>(),
  });

  const stats = useMemo(() => {
    const bySource: Record<string, number> = {};
    leads.forEach((l) => {
      const src = l.utmSource ?? '(direto)';
      bySource[src] = (bySource[src] ?? 0) + 1;
    });
    const top4 = Object.entries(bySource)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4);
    return { total: leads.length, bySource: top4 };
  }, [leads]);

  const handleExport = async () => {
    const apiBase = (import.meta as any).env?.VITE_API_URL ?? '/v1';
    const res = await fetch(`${apiBase}/admin/leads/export`, {
      headers: { Authorization: `Bearer ${tokenStore.getAccess()}` },
    });
    if (!res.ok) {
      alert('Falha ao exportar.');
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <AdminPageHeader
        title="Leads"
        subtitle={`${stats.total} pessoas no banco · capturadas no banner Beta`}
        action={
          <Button onClick={handleExport} className="bg-teal-500 hover:bg-teal-600 text-white">
            📥 Exportar CSV
          </Button>
        }
      />

      {/* Stats por UTM source */}
      {stats.bySource.length > 0 && (
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {stats.bySource.map(([source, count]) => (
            <div key={source} className="bg-white rounded-2xl p-4 border border-slate-100">
              <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold truncate">{source}</p>
              <p className="text-2xl font-extrabold text-slate-700 mt-1 tabular-nums">{count}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">
                {((count / stats.total) * 100).toFixed(0)}% do total
              </p>
            </div>
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="bg-white rounded-3xl p-12 text-center border border-slate-100">
          <p className="text-slate-400 text-sm">Carregando...</p>
        </div>
      ) : leads.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-sm">
          <p className="text-slate-500">Nenhum lead capturado ainda.</p>
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-slate-50">
                <tr>
                  <Th>Nome</Th>
                  <Th>E-mail</Th>
                  <Th>UTM</Th>
                  <Th>Referência</Th>
                  <Th>Data</Th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-t border-slate-100">
                    <td className="px-5 py-3 font-medium text-slate-700">{l.name}</td>
                    <td className="px-5 py-3 text-slate-600">{l.email}</td>
                    <td className="px-5 py-3 text-slate-500 text-xs">
                      {[l.utmSource, l.utmMedium, l.utmCampaign].filter(Boolean).join(' / ') || '—'}
                    </td>
                    <td className="px-5 py-3 text-slate-500 text-xs truncate max-w-xs">{l.referrer ?? '—'}</td>
                    <td className="px-5 py-3 text-slate-500 text-xs whitespace-nowrap">
                      {new Date(l.createdAt).toLocaleDateString('pt-BR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-left px-5 py-3 text-slate-500 font-semibold uppercase text-xs">{children}</th>;
}
